import { styled } from '@mui/material/styles';
import { CommentType } from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import SelectMenu from 'components/common/menu/SelectMenu';
import linkifyHtml from 'linkify-html';
import { decode } from 'html-entities';
import { useEffect, useMemo, useRef, useState } from 'react';
import More2FillIcon from 'remixicon-react/More2FillIcon';
import { catchErrorMessage, formatDate, highlightSearchKeyword } from 'utility/helpers';
import Avatar from 'components/common/avatar/Avatar';
import { PROJECT, USER_PROFILE } from 'constants/routes';
import { UserLink } from '@buildwise/pages/knowledge/components/DraftStatusMessage';
import { useSearchParams } from 'react-router-dom';
import LinkIcon from 'remixicon-react/LinkIcon';
import Edit2LineIcon from 'remixicon-react/Edit2LineIcon';
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon';
import { theme } from 'config/theme';
import { toast } from 'react-toastify';
import useUserData from 'hooks/useUserData';
import KnowledgeAPI from 'api/KnowledgeAPI';
import { useActivityLogQuery } from '../../queries';
import ActivityForm from './ActivityForm';

type RenderLogProps = {
  log: CommentType;
  searchKeyword: string;
};

function RenderLog({ log, searchKeyword }: RenderLogProps) {
  const articleDescriptionRef = useRef<HTMLParagraphElement | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  const scrollToLogId = useMemo(
    () => decodeURIComponent(searchParams?.get('logId') || ''),
    [searchParams],
  );
  const [isLinkCopied, setLinkCopied] = useState(false);
  const [isEditingLog, setIsEditingLog] = useState(false);
  const { user, isNovaOrCompanyAdmin, companyId } = useUserData();
  const { refetch } = useActivityLogQuery({
    companyId,
    objectId: log?.objectId || '',
    shouldFetch: false,
  });

  useEffect(() => {
    if (scrollToLogId && logRef.current) {
      logRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logRef.current, scrollToLogId]);

  useEffect(() => {
    if (articleDescriptionRef.current && scrollToLogId === log.id) {
      articleDescriptionRef.current.className = `${articleDescriptionRef.current.className} scroll-highlight`;
    }

    setTimeout(() => {
      if (articleDescriptionRef.current) {
        articleDescriptionRef.current.className = articleDescriptionRef.current.className.replace(
          'scroll-highlight',
          '',
        );
      }
    }, 4000);
  }, [articleDescriptionRef.current]);

  // const handleEmbeddedImageClick = useCallback((event: unknown) => {
  //     const attachmentId =
  //       (event as MouseEvent)?.currentTarget?.getAttribute('data-attachment-id') ||
  //       (event as MouseEvent)?.currentTarget?.getAttribute('attachmentid');
  //     const fileName = mediaFiles.find((file) => file.id === attachmentId)?.fileName;

  //     if (fileName) {
  //       dispatch({
  //         type: 'TOGGLE_LIGHTBOX',
  //         payload: { open: true, fileName },
  //       });
  //     }
  //   }, []);

  // useEffect(() => {
  //     if (articleDescriptionRef.current) {
  //       // Add click event for embedded images
  //       const embeddedImages = articleDescriptionRef.current.querySelectorAll('img');

  //       embeddedImages.forEach((embeddedImage) => {
  //         embeddedImage.style.cursor = 'pointer';
  //         embeddedImage.addEventListener('click', handleEmbeddedImageClick);
  //       });

  //       // Add click event for hashtags
  //       const hashtags = articleDescriptionRef.current.querySelectorAll('span.hashtag');

  //       hashtags.forEach((hashtag) => {
  //         hashtag.addEventListener('click', handleHashtagClick);
  //       });
  //     }

  //     return () => {
  //       if (articleDescriptionRef.current) {
  //         // Remove click event for embedded images
  //         const embeddedImages = articleDescriptionRef.current.querySelectorAll('img');

  //         embeddedImages.forEach((embeddedImage) =>
  //           embeddedImage.removeEventListener('click', handleEmbeddedImageClick),
  //         );

  //         // Remove click event for hashtags
  //         const hashtags = articleDescriptionRef.current.querySelectorAll('span.hashtag');

  //         hashtags.forEach((hashtag) => {
  //           hashtag.removeEventListener('click', handleHashtagClick);
  //         });
  //       }
  //     };
  //   }, [articleDescriptionRef, article.description])

  const deleteComment = async () => {
    try {
      await KnowledgeAPI.deleteComment(log.id);
      refetch();
      toast.success(`Activity Log deleted successfully!`);
    } catch (error) {
      toast.error(catchErrorMessage(error));
    }
  };

  return (
    <Box my={1} ref={log.id === scrollToLogId ? logRef : null}>
      {isEditingLog ? (
        <ActivityForm setOpen={setIsEditingLog} log={log} />
      ) : (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography
              sx={(theme) => ({
                color: theme.palette.custom.tuna,
                fontSize: 14,
                fontWeight: 700,
              })}
            >
              {log.objectSubType}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                title="Created On"
                sx={(theme) => ({
                  color: theme.palette.custom.mistBlue,
                  fontSize: 14,
                  whiteSpace: 'nowrap',
                })}
              >
                {formatDate(log.commentedAt)}
              </Typography>
              <SelectMenu
                menu={[
                  {
                    title: 'Edit',
                    icon: <Edit2LineIcon size={17} />,
                    onClick: () => {
                      setIsEditingLog(true);
                    },
                    isHidden: user?.username !== log.commentAuthor && !isNovaOrCompanyAdmin,
                  },
                  {
                    title: 'Copy Link',
                    icon: <LinkIcon size={17} />,
                    onClick: async () => {
                      try {
                        await navigator.clipboard.writeText(
                          `${window.location.origin}${PROJECT.replace(':projectId', log.objectId || '')}?logId=${log.id}`,
                        );
                        setLinkCopied(true);

                        setTimeout(() => {
                          setLinkCopied(false);
                        }, 2000);
                      } catch (error) {
                        console.log('COPY_COMMENT_LINK_ERROR', error);
                      }
                    },
                    isHidden: false,
                  },
                  {
                    title: 'Delete',
                    titleColor: theme.palette.custom.redWine,
                    icon: <DeleteBinLineIcon color={theme.palette.custom.redWine} size={17} />,
                    onClick: deleteComment,
                    isHidden: user?.username !== log.commentAuthor && !isNovaOrCompanyAdmin,
                  },
                ]}
              >
                <Tooltip
                  open={isLinkCopied}
                  placement="top"
                  title={
                    <Typography fontSize={14} fontWeight={600}>
                      Link Copied
                    </Typography>
                  }
                >
                  <IconButton size="small">
                    <More2FillIcon size={20} />
                  </IconButton>
                </Tooltip>
              </SelectMenu>
            </Box>
          </Box>
          <LogText
            ref={articleDescriptionRef}
            dangerouslySetInnerHTML={{
              __html: linkifyHtml(
                decode(
                  searchKeyword
                    ? highlightSearchKeyword(log.objectDescription || '', searchKeyword)
                    : log.objectDescription,
                ),
                { target: '_blank' },
              ),
            }}
          />
          <Box display="flex" alignItems="center" title="Created By" my={1.5}>
            <Avatar
              src={log?.profileImageURL}
              size={24}
              fontSize={12}
              name={log.commentAuthorDisplayName || log.commentAuthor}
              to={USER_PROFILE.replace(':userName', log?.commentAuthor || '')}
            />
            <UserLink
              mail={log.commentAuthor}
              name={log.commentAuthorDisplayName || log.commentAuthor}
              sx={(theme) => ({
                color: theme.palette.custom.tuna,
                fontSize: 14,
                fontWeight: 500,
                ml: 0.75,
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
              })}
            />
          </Box>
          <Divider sx={{ mt: 1 }} />
        </>
      )}
    </Box>
  );
}

const LogText = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: 8,
  wordBreak: 'break-word',
  fontFamily: 'Lato, sans-serif',
  fontSize: 14,
  color: theme.palette.custom.tuna,
  '& p': {
    margin: '0 0 8px',
  },
  '& *': {
    fontFamily: 'Lato, sans-serif !important',
    maxWidth: '100% !important',
  },
  '& .highlight': {
    backgroundColor: `${theme.palette.custom.paleGold} !important`,
  },
  '&.scroll-highlight': {
    borderRadius: 4,
    animation: 'highlight-log 4s',
  },
  '@keyframes highlight-log': {
    '0%': {
      backgroundColor: 'rgba(255, 193, 7, 0)',
    },
    '30%': {
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
    },
    '50%': {
      backgroundColor: 'rgba(255, 193, 7, 0)',
    },
    '70%': {
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
    },
    '100%': {
      backgroundColor: 'rgba(255, 193, 7, 0)',
    },
  },
  '& a': {
    whiteSpace: 'pre-wrap',
    color: `${theme.palette.custom.link} !important`,
    '&:hover': {
      textDecoration: 'underline !important',
    },
  },
  '& span.mention, & span.hashtag': {
    backgroundColor: theme.palette.custom.aliceBlue,
    color: theme.palette.custom.link,
    borderRadius: 4,
    padding: 2,
  },
  '& span.mention': {
    marginRight: 4,
  },
  '& span.hashtag:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

export default RenderLog;
